import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild,
  inject,
} from '@angular/core';

import {
  IDateRange,
  addDays,
  addMonths,
  clampDate,
  daysBetween,
  endOfMonth,
  formatMonthLabel,
  formatRangeLabel,
  parseISO,
  parseRange,
  sameDay,
  serializeRange,
  startOfMonth,
  toISO,
} from '../../models/date-range.model';

/** Una celda del calendario: un día del mes visible o el hueco de relleno. */
export interface IDayCell {
  fecha: Date;
  iso: string;
  dia: number;
  /** false = día de relleno del mes anterior o siguiente. */
  delMes: boolean;
  deshabilitado: boolean;
}

/** Atajo del panel: nombre y resolución del rango en fechas. */
interface IPreset {
  id: string;
  label: string;
  resolve: (hoy: Date) => IDateRange;
}

/** Lunes primero, como cualquier calendario mexicano. */
const DIAS_SEMANA: ReadonlyArray<string> = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

/** Contador de instancias: da un id único al panel para el `aria-controls`. */
let nextPanelId = 0;

const rango = (desde: Date, hasta: Date): IDateRange =>
  ({ desde: toISO(desde), hasta: toISO(hasta) });

/**
 * Los atajos se resuelven contra el último día con datos, no contra la fecha
 * del sistema: el tablero es una demo fechada en agosto de 2026 y «este mes»
 * calculado con el reloj real dejaría todas las gráficas vacías.
 */
const PRESETS: ReadonlyArray<IPreset> = [
  { id: '7d',   label: 'Últimos 7 días',   resolve: (hoy) => rango(addDays(hoy, -6), hoy) },
  { id: '30d',  label: 'Últimos 30 días',  resolve: (hoy) => rango(addDays(hoy, -29), hoy) },
  { id: 'mes',  label: 'Este mes',         resolve: (hoy) => rango(startOfMonth(hoy), hoy) },
  { id: 'prev', label: 'Mes anterior',     resolve: (hoy) => {
    const anterior = addMonths(startOfMonth(hoy), -1);
    return rango(anterior, endOfMonth(anterior));
  } },
  { id: 'tri',  label: 'Últimos 3 meses',  resolve: (hoy) => rango(addMonths(startOfMonth(hoy), -2), hoy) },
  { id: 'anio', label: 'Este año',         resolve: (hoy) => rango(new Date(hoy.getFullYear(), 0, 1), hoy) },
  { id: '12m',  label: 'Últimos 12 meses', resolve: (hoy) => rango(addMonths(startOfMonth(hoy), -11), hoy) },
];

/**
 * Selector de rango de fechas.
 *
 * Está escrito a mano, como `<app-multi-select>`, en vez de traer el
 * datepicker de ng-bootstrap: el componente de librería llega con su propia
 * hoja de estilos y sus colores literales, y reteñirlo entero para el cristal
 * sale más caro —y más frágil— que pintar una rejilla de siete columnas con
 * los tokens del proyecto.
 *
 * El valor entra y sale serializado (`YYYY-MM-DD|YYYY-MM-DD`) para que quepa
 * en el `Record<string, Array<string>>` de los filtros sin abrir esa API.
 */
@Component({
  selector: 'app-date-range',
  standalone: false,
  templateUrl: './date-range.component.html',
  styleUrl: './date-range.component.scss',
})
export class DateRangeComponent implements OnChanges {

  /** Rango vigente, serializado. Cadena vacía = sin rango. */
  @Input() public value = '';

  /** Extremos seleccionables, en ISO. Fuera de ellos no hay datos que mostrar. */
  @Input() public min = '';
  @Input() public max = '';

  @Input() public placeholder = 'dd/mm/aaaa – dd/mm/aaaa';

  /** Etiqueta del campo que lo envuelve; da nombre accesible al control. */
  @Input() public label = 'Rango de fechas';

  @Output() public valueChange = new EventEmitter<string>();

  public open = false;

  public readonly panelId = `z-daterange-panel-${++nextPanelId}`;
  public readonly diasSemana = DIAS_SEMANA;
  public readonly presets = PRESETS;

  /** Primer día del mes que se está pintando. */
  public mesVisible: Date = new Date();

  /** Extremo ya elegido mientras se está armando un rango nuevo. */
  public inicio: Date | null = null;

  /** Día bajo el cursor: pinta la banda tentativa antes del segundo clic. */
  public hover: Date | null = null;

  /** Día que recibe el tabulador dentro de la rejilla (tabindex móvil). */
  public activo: Date = new Date();

  private confirmado: IDateRange | null = null;

  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLElement>;

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR CICLO DE VIDA                            */
  /* -------------------------------------------------------------------------- */

  public ngOnChanges(): void {
    this.confirmado = parseRange(this.value);
    this.inicio = null;
    this.hover = null;

    const foco = this.confirmado
      ? parseISO(this.confirmado.hasta)!
      : (this.limiteMax ?? new Date());

    this.mesVisible = startOfMonth(foco);
    this.activo = foco;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR LECTURAS                               */
  /* -------------------------------------------------------------------------- */

  public get etiqueta(): string {
    return formatRangeLabel(this.value);
  }

  public get tituloMes(): string {
    return formatMonthLabel(this.mesVisible);
  }

  /** «31 días» — cuánto abarca lo que se está por aplicar. */
  public get resumen(): string {
    const activo = this.rangoEnCurso;

    if (!activo) {
      return this.inicio ? 'Elige la fecha final' : '';
    }

    const desde = parseISO(activo.desde)!;
    const hasta = parseISO(activo.hasta)!;
    const dias = daysBetween(desde, hasta);

    return dias === 1 ? '1 día' : `${dias} días`;
  }

  private get limiteMin(): Date | null {
    return parseISO(this.min);
  }

  private get limiteMax(): Date | null {
    return parseISO(this.max);
  }

  /** Rango que hay que pintar: el confirmado, o el que se está armando. */
  private get rangoEnCurso(): IDateRange | null {
    if (!this.inicio) {
      return this.confirmado;
    }

    if (!this.hover) {
      return { desde: toISO(this.inicio), hasta: toISO(this.inicio) };
    }

    return this.hover < this.inicio
      ? { desde: toISO(this.hover), hasta: toISO(this.inicio) }
      : { desde: toISO(this.inicio), hasta: toISO(this.hover) };
  }

  /**
   * Seis semanas fijas (42 celdas). Un mes que empieza en domingo y tiene 31
   * días ocupa seis filas; con cinco la rejilla cambiaría de alto al navegar y
   * el panel daría un salto.
   */
  public get semanas(): Array<Array<IDayCell>> {
    const primero = startOfMonth(this.mesVisible);
    // getDay() da 0 el domingo; con la semana en lunes el domingo es el 6.
    const desplazamiento = (primero.getDay() + 6) % 7;
    const arranque = addDays(primero, -desplazamiento);

    const semanas: Array<Array<IDayCell>> = [];

    for (let fila = 0; fila < 6; fila++) {
      const semana: Array<IDayCell> = [];

      for (let columna = 0; columna < 7; columna++) {
        const fecha = addDays(arranque, fila * 7 + columna);

        semana.push({
          fecha,
          iso: toISO(fecha),
          dia: fecha.getDate(),
          delMes: fecha.getMonth() === this.mesVisible.getMonth(),
          deshabilitado: this.fueraDeLimites(fecha),
        });
      }

      semanas.push(semana);
    }

    return semanas;
  }

  public get puedeRetroceder(): boolean {
    const min = this.limiteMin;

    return !min || startOfMonth(min) < this.mesVisible;
  }

  public get puedeAvanzar(): boolean {
    const max = this.limiteMax;

    return !max || startOfMonth(max) > this.mesVisible;
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR ESTADO DE UN DÍA                       */
  /* -------------------------------------------------------------------------- */

  public esInicio(celda: IDayCell): boolean {
    const activo = this.rangoEnCurso;

    return !!activo && activo.desde === celda.iso;
  }

  public esFin(celda: IDayCell): boolean {
    const activo = this.rangoEnCurso;

    return !!activo && activo.hasta === celda.iso;
  }

  public enRango(celda: IDayCell): boolean {
    const activo = this.rangoEnCurso;

    return !!activo && celda.iso > activo.desde && celda.iso < activo.hasta;
  }

  public esFoco(celda: IDayCell): boolean {
    return sameDay(celda.fecha, this.activo);
  }

  /** Nombre accesible de la celda: la fecha completa, no sólo el número. */
  public etiquetaDia(celda: IDayCell): string {
    return `${celda.dia} de ${formatMonthLabel(celda.fecha)}`;
  }

  private fueraDeLimites(fecha: Date): boolean {
    const min = this.limiteMin;
    const max = this.limiteMax;

    return (!!min && fecha < min) || (!!max && fecha > max);
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR APERTURA                              */
  /* -------------------------------------------------------------------------- */

  public toggle(): void {
    this.open ? this.close() : this.abrir();
  }

  public abrir(): void {
    this.open = true;
    this.inicio = null;
    this.hover = null;
  }

  public close(devolverFoco = true): void {
    if (!this.open) {
      return;
    }

    this.open = false;
    this.inicio = null;
    this.hover = null;

    if (devolverFoco) {
      this.triggerRef?.nativeElement.focus();
    }
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.close(false);
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR NAVEGACIÓN                             */
  /* -------------------------------------------------------------------------- */

  public mesAnterior(): void {
    if (this.puedeRetroceder) {
      this.mesVisible = addMonths(this.mesVisible, -1);
    }
  }

  public mesSiguiente(): void {
    if (this.puedeAvanzar) {
      this.mesVisible = addMonths(this.mesVisible, 1);
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR SELECCIÓN                              */
  /* -------------------------------------------------------------------------- */

  public onDia(celda: IDayCell): void {
    if (celda.deshabilitado) {
      return;
    }

    this.activo = celda.fecha;

    if (!this.inicio) {
      this.inicio = celda.fecha;
      this.hover = celda.fecha;
      // Tocar un día de relleno navega al mes al que pertenece: si no, el día
      // elegido se sale de la vista en cuanto se pinta.
      this.mesVisible = startOfMonth(celda.fecha);

      return;
    }

    const [desde, hasta] = celda.fecha < this.inicio
      ? [celda.fecha, this.inicio]
      : [this.inicio, celda.fecha];

    this.inicio = null;
    this.hover = null;
    this.emitir(serializeRange(desde, hasta));
    this.close();
  }

  public onHover(celda: IDayCell): void {
    if (this.inicio && !celda.deshabilitado) {
      this.hover = celda.fecha;
    }
  }

  public aplicarPreset(preset: IPreset): void {
    const referencia = this.limiteMax ?? new Date();
    const resuelto = preset.resolve(referencia);
    const min = this.limiteMin;
    const max = this.limiteMax;

    const desde = clampDate(parseISO(resuelto.desde)!, min, max);
    const hasta = clampDate(parseISO(resuelto.hasta)!, min, max);

    this.inicio = null;
    this.hover = null;
    this.emitir(serializeRange(desde, hasta));
    this.close();
  }

  public limpiar(event?: Event): void {
    event?.stopPropagation();
    this.inicio = null;
    this.hover = null;
    this.emitir('');
    this.close(false);
  }

  private emitir(value: string): void {
    this.value = value;
    this.confirmado = parseRange(value);
    this.valueChange.emit(value);
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR TECLADO                               */
  /* -------------------------------------------------------------------------- */

  /** Abre con Enter, Espacio o flecha abajo, igual que el combo de filtros. */
  public onTriggerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.abrir();
      setTimeout(() => this.enfocarActivo());

      return;
    }

    if (event.key === 'Escape' && this.open) {
      event.preventDefault();
      this.close();
    }
  }

  /**
   * Tabindex móvil sobre la rejilla: sólo un día es parada del tabulador y las
   * flechas mueven el foco. Con 42 botones tabulables, llegar al pie del panel
   * costaría 42 pulsaciones — y la trampa de foco del cajón de filtros las
   * contaría todas.
   */
  public onGridKeydown(event: KeyboardEvent): void {
    const saltos: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };

    if (event.key in saltos) {
      event.preventDefault();
      this.moverFoco(addDays(this.activo, saltos[event.key]));

      return;
    }

    switch (event.key) {
      case 'Home':
        event.preventDefault();
        this.moverFoco(startOfMonth(this.activo));
        return;

      case 'End':
        event.preventDefault();
        this.moverFoco(endOfMonth(this.activo));
        return;

      case 'PageUp':
        event.preventDefault();
        this.moverFoco(addMonths(this.activo, -1));
        return;

      case 'PageDown':
        event.preventDefault();
        this.moverFoco(addMonths(this.activo, 1));
        return;

      case 'Escape':
        // El cajón de filtros sólo se cierra si nadie consumió la tecla.
        event.preventDefault();
        this.close();
        return;

      default:
        return;
    }
  }

  private moverFoco(destino: Date): void {
    const acotado = clampDate(destino, this.limiteMin, this.limiteMax);

    this.activo = acotado;

    if (acotado.getMonth() !== this.mesVisible.getMonth()
      || acotado.getFullYear() !== this.mesVisible.getFullYear()) {
      this.mesVisible = startOfMonth(acotado);
    }

    if (this.inicio) {
      this.hover = acotado;
    }

    setTimeout(() => this.enfocarActivo());
  }

  private enfocarActivo(): void {
    this.elementRef.nativeElement
      .querySelector<HTMLButtonElement>('.day[tabindex="0"]')
      ?.focus();
  }
}
